import {
  Goal,
  goalInboxID,
  goalsArchivedSectionID,
  goalsCompletedSectionID,
  ID,
  rootId,
  TaskListItemTypes,
  TaskListTreeItem,
} from '@griply/types';

import { getArchivedGoalIds } from '../../derived-goal-state';
import { getGoalParentIds } from '../get-goal-parents';
import { orderGoalTreeChildren } from './order-goal-tree-children';
import { BuildGoalListTreeOptions } from './types';

export type BuildGoalGroupingGoalListTreeOptions = BuildGoalListTreeOptions & {
  // All goals (any type), in display order — supplies the section header name
  // for each parent goal and the order sections appear in.
  parentGoals: { id: ID; name: string }[];
  ungroupedSectionName: string;
  // On: sections are root goals and sub-goals nest as folders down to their projects.
  nestGoals?: boolean;
};

// Groups goals (projects) by the direct parent goal they are linked to
// (`ancestorIds[last]`). Goals with no resolvable parent land in an "ungrouped"
// section; archived goals land in the archived section at the bottom.
export const buildGoalGroupingGoalListTree = (
  goals: Goal[],
  {
    hideArchived,
    hideCompleted,
    ungroupedSectionName,
    parentGoals,
    allGoals,
    links,
    goalChildrenByParentId,
    nestGoals = false,
  }: BuildGoalGroupingGoalListTreeOptions,
): Record<ID, TaskListTreeItem> => {
  // "archived" is inherited: an archived ancestor archives the whole subtree
  const archivedIds = getArchivedGoalIds(allGoals, { links });
  const activeGoals: Goal[] = [];
  const archivedGoals: Goal[] = [];
  const completedGoals: Goal[] = [];

  goals.forEach((goal) => {
    if (hideArchived && archivedIds.has(goal.id)) {
      return;
    }
    if (goal.archivedAt) {
      archivedGoals.push(goal);
      return;
    }
    // A completed goal never sits inline among its live siblings; it moves to its own section.
    if (goal.completedAt) {
      if (!hideCompleted) {
        completedGoals.push(goal);
      }
      return;
    }
    activeGoals.push(goal);
  });

  const parentNameById = new Map(parentGoals.map(({ id, name }) => [id, name]));

  const treeItems: Record<ID, TaskListTreeItem> = {};

  const ensureSection = (id: ID, name: string) => {
    if (!treeItems[id]) {
      treeItems[id] = {
        id,
        type: TaskListItemTypes.LifeArea,
        name,
        children: [],
        isFolder: true,
      };
    }
  };

  ensureSection(goalInboxID, ungroupedSectionName);

  const goalsById = new Map(allGoals.map((goal) => [goal.id, goal]));
  const namedParentIds = (goalId: ID): ID[] => {
    const goal = goalsById.get(goalId);
    return goal
      ? getGoalParentIds(goal, { links }).filter(
          (parentId) => parentNameById.get(parentId) !== undefined,
        )
      : [];
  };

  // A goal with no named parent heads a section; any other goal is a folder inside its parents'.
  const ensureGoalNode = (goalId: ID) => {
    if (treeItems[goalId]) {
      return;
    }
    const name = parentNameById.get(goalId) as string;
    if (!nestGoals || !namedParentIds(goalId).length) {
      ensureSection(goalId, name);
      return;
    }
    treeItems[goalId] = {
      id: goalId,
      type: TaskListItemTypes.Goal,
      name,
      children: [],
      isFolder: true,
    };
  };

  const pushChild = (parentId: ID, childId: ID) => {
    const children = treeItems[parentId].children;
    if (children && !children.includes(childId)) {
      children.push(childId);
    }
  };

  activeGoals.forEach((goal) => {
    treeItems[goal.id] = {
      id: goal.id,
      type: TaskListItemTypes.Goal,
      name: goal.name,
      children: [],
      isFolder: false,
    };

    // One goal id under several parents renders once per parent — the tree list keys nodes by path.
    const parentIds = namedParentIds(goal.id);

    if (parentIds.length) {
      parentIds.forEach((parentId) => {
        ensureGoalNode(parentId);
        pushChild(parentId, goal.id);
      });
      return;
    }

    treeItems[goalInboxID].children?.push(goal.id);
  });

  if (nestGoals) {
    // Walk each folder up to its root section; `visited` guards a corrupt parent cycle.
    const visited = new Set<ID>();
    const attach = (goalId: ID) => {
      if (visited.has(goalId)) {
        return;
      }
      visited.add(goalId);
      namedParentIds(goalId).forEach((parentId) => {
        ensureGoalNode(parentId);
        pushChild(parentId, goalId);
        attach(parentId);
      });
    };
    Object.values(treeItems)
      .filter(
        ({ type, isFolder }) => type === TaskListItemTypes.Goal && isFolder,
      )
      .forEach(({ id }) => attach(id));
  }

  if (completedGoals.length) {
    treeItems[goalsCompletedSectionID] = {
      id: goalsCompletedSectionID,
      type: TaskListItemTypes.LifeArea,
      name: '',
      children: completedGoals.map((goal) => goal.id),
      isFolder: true,
    };
  }

  if (archivedGoals.length) {
    treeItems[goalsArchivedSectionID] = {
      id: goalsArchivedSectionID,
      type: TaskListItemTypes.LifeArea,
      name: '',
      children: archivedGoals.map((goal) => goal.id),
      isFolder: true,
    };
  }

  const rootChildren = parentGoals.reduce<ID[]>((acc, { id }) => {
    const node = treeItems[id];
    if (
      id !== goalInboxID &&
      node?.type === TaskListItemTypes.LifeArea &&
      node.children?.length
    ) {
      acc.push(id);
    }
    return acc;
  }, []);

  if (treeItems[goalInboxID].children?.length) {
    rootChildren.push(goalInboxID);
  }

  if (completedGoals.length) {
    rootChildren.push(goalsCompletedSectionID);
  }

  if (archivedGoals.length) {
    rootChildren.push(goalsArchivedSectionID);
  }

  orderGoalTreeChildren(treeItems, goalChildrenByParentId);

  if (nestGoals) {
    // Projects sit above sub-goal folders in every group; the order map ranks within each
    // bucket. This must run after `orderGoalTreeChildren`, which rebuilds `children` from a
    // goal's stored `goalSorting` — an array that predates projects and cannot rank them.
    Object.values(treeItems).forEach((node) => {
      if (!node.children?.length) {
        return;
      }

      const leaves = node.children.filter((id) => !treeItems[id]?.isFolder);
      const folders = node.children.filter((id) => treeItems[id]?.isFolder);

      if (!leaves.length || !folders.length) {
        return;
      }

      node.children = [...leaves, ...folders];
    });
  }

  return {
    [rootId]: {
      id: rootId,
      name: 'Root',
      type: TaskListItemTypes.Root,
      children: rootChildren,
    },
    ...treeItems,
  };
};
